import React, { useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { sentPostMessage } from "../../helper";
import { useApp } from "../../context/AppState";
import { POPUP_SECTIONS } from "../../constant/constant";
import { DIVIDER_EDITOR_MIN_HEIGHT } from "../Preview/Draggable/Block/DividerBlock";
import { notifyPreviewLayoutDrag } from "../../utils/preview-layout-drag";
import {
  getPreviewBlockOutlineStyle,
  PREVIEW_BLOCK_HIGHLIGHT,
} from "../../utils/preview-block-highlight";
import { resolveIconCloseButtonLayout } from "../../utils/resolve-icon-close-button-layout";
import { isSectionVisible } from "../../../shared/section-visibility.js";

const coordsMatch = (a, b) =>
  Number(a?.x) === Number(b?.x) && Number(a?.y) === Number(b?.y);

const dimensionsMatch = (a, b) =>
  Number(a?.width) === Number(b?.width) &&
  Number(a?.height) === Number(b?.height);

const DraggableWrapper = ({
  lockDraggable,
  section,
  children,
  isSelected,
  onClick,
  index,
  isSubSection,
  subIndex,
  sectionLockDraggable,
  isSliderVerical,
  layoutFlow = false,
}) => {
  const { appState, isMobile: viewportIsMobile, previewDeviceIsMobile } = useApp();
  const isMobile =
    appState?.is_preview_mode && typeof previewDeviceIsMobile === "boolean"
      ? previewDeviceIsMobile
      : viewportIsMobile;

  const [updateDisplacement, setUpdateDisplacement] = useState(null);
  const [updateDimension, setUpdateDimension] = useState(null);
  const [updateLayer, setUpdateLayer] = useState(null);

  const [resizing, setResizing] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [currentDisplacement, setCurrentDisplacement] = useState(null);
  const [currentDimension, setCurrentDimension] = useState(null);
  const [currentLayer, setCurrentLayer] = useState(null);

  const externalDisplacement =
    section?.setting?.position?.displacement?.[isMobile ? "mobile" : "desktop"];
  const externalDimension =
    section?.setting?.dimension?.[isMobile ? "mobile" : "desktop"];
  const externalLayer =
    section?.setting?.layer?.[isMobile ? "mobile" : "desktop"];

  const pendingDisplacementRef = useRef(null);
  const pendingDimensionRef = useRef(null);

  useEffect(() => {
    pendingDisplacementRef.current = null;
    pendingDimensionRef.current = null;
    setCurrentDisplacement(null);
    setUpdateDisplacement(null);
    setCurrentDimension(null);
    setUpdateDimension(null);
    setCurrentLayer(null);
    setUpdateLayer(null);
  }, [index, subIndex, isMobile]);

  useEffect(() => {
    if (dragging || resizing) return;

    const pendingDisp = pendingDisplacementRef.current;
    if (pendingDisp && externalDisplacement) {
      if (coordsMatch(pendingDisp, externalDisplacement)) {
        pendingDisplacementRef.current = null;
        setCurrentDisplacement(null);
        setUpdateDisplacement(null);
      }
      return;
    }

    const pendingDim = pendingDimensionRef.current;
    if (pendingDim && externalDimension) {
      if (dimensionsMatch(pendingDim, externalDimension)) {
        pendingDimensionRef.current = null;
        setCurrentDimension(null);
        setUpdateDimension(null);
      }
      return;
    }

    if (!pendingDisp && !pendingDim) {
      setCurrentDisplacement(null);
      setCurrentDimension(null);
    }
  }, [
    dragging,
    resizing,
    externalDisplacement?.x,
    externalDisplacement?.y,
    externalDimension?.width,
    externalDimension?.height,
  ]);

  const iconCloseLayout = useMemo(
    () => resolveIconCloseButtonLayout(section, isMobile),
    [section, isMobile]
  );

  const position = useMemo(
    () =>
      currentDisplacement ??
      iconCloseLayout?.displacement ??
      section?.setting?.position?.displacement?.[
        isMobile ? "mobile" : "desktop"
      ] ?? { x: 0, y: 0 },
    [currentDisplacement, iconCloseLayout, section, isMobile]
  );

  const dimension = useMemo(() => {
    const raw =
      currentDimension ??
      iconCloseLayout?.dimension ??
      section?.setting?.dimension?.[isMobile ? "mobile" : "desktop"] ?? {
        width: 100,
        height: 100,
      };

    if (section?.type === POPUP_SECTIONS.DIVIDER) {
      return {
        ...raw,
        height: Math.max(Number(raw.height) || 0, DIVIDER_EDITOR_MIN_HEIGHT),
      };
    }

    return raw;
  }, [currentDimension, iconCloseLayout, section, isMobile]);

  const layer = useMemo(
    () =>
      currentLayer ??
      section?.setting?.layer?.[isMobile ? "mobile" : "desktop"] ?? {
        zIndex: 1,
      },
    [currentLayer, section, isMobile]
  );

  const transformOrigin = useMemo(
    () => ({
      x: position?.x + dimension?.width / 2,
      y: position?.y + dimension?.height / 2,
    }),
    [position, dimension]
  );

  const handleDrag = (e, data) => {
    if (!resizing && !lockDraggable && !sectionLockDraggable) {
      setCurrentDisplacement({ x: data.x, y: data.y });
    }
  };

  const handleDragStop = (e, data) => {
    if (!resizing && !lockDraggable && !sectionLockDraggable) {
      const next = { x: data.x, y: data.y };
      setCurrentDisplacement(next);
      pendingDisplacementRef.current = next;
      setUpdateDisplacement(next);
      setDragging(false);
    }
  };

  const commitLayoutDragEnd = () => {
    if (typeof window !== "undefined") {
      window.__WEUPSELL_PREVIEW_LAYOUT_DRAGGING__ = false;
    }
    if (appState?.is_preview_mode) {
      notifyPreviewLayoutDrag(false);
    }
  };

  const beginLayoutInteraction = () => {
    if (typeof window !== "undefined") {
      window.__WEUPSELL_PREVIEW_LAYOUT_DRAGGING__ = true;
    }
    if (appState?.is_preview_mode) {
      notifyPreviewLayoutDrag(true);
    }
  };

  const handleResize = (e, direction) => {
    e.stopPropagation();
    beginLayoutInteraction();
    setResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = dimension?.width;
    const startHeight = dimension?.height;
    const startPosition = position;

    let newWidth = startWidth;
    let newHeight = startHeight;
    let newX = startPosition?.x;
    let newY = startPosition?.y;

    const onMouseMove = (event) => {
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      switch (direction) {
        case "top-left":
          newWidth = startWidth - dx;
          newHeight = startHeight - dy;
          if (!isSubSection) {
            newX = startPosition?.x + dx;
            newY = startPosition?.y + dy;
          }
          break;
        case "bottom-left":
          newWidth = startWidth - dx;
          newHeight = startHeight + dy;
          if (!isSubSection) {
            newX = startPosition?.x + dx;
          }
          break;
        case "top-right":
          newWidth = startWidth + dx;
          newHeight = startHeight - dy;
          if (!isSubSection) {
            newY = startPosition?.y + dy;
          }
          break;
        case "bottom-right":
          newWidth = startWidth + dx;
          newHeight = startHeight + dy;
          break;
        case "left":
          newWidth = startWidth - dx;
          if (!isSubSection) {
            newX = startPosition?.x + dx;
          }
          break;
        case "right":
          newWidth = startWidth + dx;
          break;
        case "top":
          newHeight = startHeight - dy;
          if (!isSubSection) {
            newY = startPosition?.y + dy;
          }
          break;
        case "bottom":
          newHeight = startHeight + dy;
          break;
        default:
          break;
      }

      setCurrentDimension({
        width: Math.max(newWidth, 20),
        height: Math.max(newHeight, 20),
      });
      setCurrentDisplacement({ x: newX, y: newY });
    };

    const onMouseUp = () => {
      const nextDim = {
        width: Math.max(newWidth, 20),
        height: Math.max(newHeight, 20),
      };
      const nextDisp = { x: newX, y: newY };
      setCurrentDimension(nextDim);
      setCurrentDisplacement(nextDisp);
      pendingDimensionRef.current = nextDim;
      pendingDisplacementRef.current = nextDisp;
      setUpdateDimension(nextDim);
      setUpdateDisplacement(nextDisp);

      setResizing(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    if (!updateDisplacement) return;
    sentPostMessage("displacement", {
      type: section?.type,
      index,
      displacement: updateDisplacement,
      isSubSection,
      subIndex,
    });
    requestAnimationFrame(() => {
      commitLayoutDragEnd();
    });
  }, [updateDisplacement]);

  useEffect(() => {
    if (!updateDimension) return;
    sentPostMessage("dimension", {
      type: section?.type,
      index,
      dimension: updateDimension,
      isSubSection,
      subIndex,
    });
    if (!updateDisplacement) {
      requestAnimationFrame(() => {
        commitLayoutDragEnd();
      });
    }
  }, [updateDimension]);

  useEffect(() => {
    if (updateLayer) {
      sentPostMessage("layer", {
        type: section?.type,
        index,
        layer: updateLayer,
        isSubSection,
        subIndex,
      });
    }
  }, [updateLayer]);

  const [isLayerPopoverOpen, setIsLayerPopoverOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isLayoutFlow = layoutFlow || section?.setting?.layoutFlow === true;
  const isPreviewMode = Boolean(appState?.is_preview_mode);

  const popoverRef = useRef(null);

  useEffect(() => {
    if (isSelected) {
      setIsHovered(false);
    }
  }, [isSelected]);

  const handleOutsideClick = (event) => {
    if (popoverRef.current && !popoverRef.current.contains(event.target)) {
      setIsLayerPopoverOpen(false);
    }
  };

  useEffect(() => {
    if (isLayerPopoverOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isLayerPopoverOpen]);

  if (!isSectionVisible(section)) {
    return null;
  }

  if (isLayoutFlow) {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          boxSizing: "border-box",
          ...(layer || {}),
          ...getPreviewBlockOutlineStyle({
            isSelected,
            isHovered,
            isPreviewMode,
          }),
        }}
        onMouseEnter={() => {
          if (isPreviewMode && !isSelected) setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          const target = e.target;
          const isInputField =
            target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.tagName === "SELECT" ||
            target.contentEditable === "true" ||
            target.closest("input, textarea, select, [contenteditable]");
          if (!isInputField) {
            setIsHovered(false);
            onClick(e);
          }
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <>
      <Draggable
        key={`draggable-${index}-${subIndex ?? "root"}`}
        position={position}
        onStart={(e) => {
          // Don't stop propagation in live mode or preview mode to allow input events
          if (!window?.isLiveModeTrigger && !appState?.is_preview_mode) {
            e.stopPropagation();
          }
          if (!lockDraggable && !sectionLockDraggable) {
            beginLayoutInteraction();
            setDragging(true);
          }
        }}
        onStop={handleDragStop}
        onDrag={handleDrag}
        disabled={
          resizing || !isSelected || lockDraggable || sectionLockDraggable
        }
        cancel=".draggable-input-field"
      >
        <div
          style={{
            position: "absolute",
            ...layer,
            ...dimension,
            transformOrigin: `${transformOrigin?.x}px ${transformOrigin?.y}px`,
            ...(isPreviewMode && !isSelected
              ? getPreviewBlockOutlineStyle({
                  isSelected: false,
                  isHovered,
                  isPreviewMode: true,
                })
              : {}),
            cursor: dragging
              ? "move"
              : !appState?.is_preview_mode
              ? "pointer"
              : "inherit",
            // Ensure input fields inside are always clickable
            pointerEvents: "auto",
          }}
          onMouseEnter={(e) => {
            if (window?.isLiveModeTrigger) return;
            if (isPreviewMode) {
              if (!isSelected) setIsHovered(true);
              return;
            }
            const targetOverlay =
              e.currentTarget.querySelector(".hover-overlay");
            if (targetOverlay) {
              targetOverlay.style.opacity = 1;
            }
          }}
          onMouseLeave={(e) => {
            if (window?.isLiveModeTrigger) return;
            if (isPreviewMode) {
              setIsHovered(false);
              return;
            }
            const targetOverlay =
              e.currentTarget.querySelector(".hover-overlay");
            if (targetOverlay) {
              targetOverlay.style.opacity = 0;
            }
          }}
        >
          {!window?.isLiveModeTrigger && (
            <div
              className="hover-overlay"
              style={{
                position: "absolute",
                zIndex: 9999,
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                backgroundColor: "rgba(92, 67, 159, 0.2)",
                border: "2px solid #3A2C6F",
                transition: "all 0.3s ease-in-out",
                pointerEvents: "none",
              }}
            />
          )}
          <div
            onClick={(e) => {
              // In live mode or preview mode, don't interfere with input field clicks
              // if (window?.isLiveModeTrigger || appState?.is_preview_mode) {
              //   return;
              // }
              
              // In editor mode, only handle clicks if the target is not an input field
              const target = e.target;
              const isInputField = target.tagName === 'INPUT' || 
                                  target.tagName === 'TEXTAREA' || 
                                  target.tagName === 'SELECT' ||
                                  target.contentEditable === 'true' ||
                                  target.closest('input, textarea, select, [contenteditable]');
              
              if (!isInputField) {
                setIsHovered(false);
                onClick(e);
              }
            }}
            style={{
              width: "100%",
              height: "100%",
              color: "transparent",
              pointerEvents: "auto",
            }}
          >
            {children}
          </div>
        </div>
      </Draggable>
      {isSelected && !window?.isLiveModeTrigger && appState?.is_preview_mode && (
        <div
          style={{
            position: "absolute",
            zIndex: 10,
            ...dimension,
            transform: `translate(${position?.x || 0}px, ${
              position?.y || 0
            }px)`,
            transformOrigin: `${transformOrigin?.x}px ${transformOrigin?.y}px`,
            border: PREVIEW_BLOCK_HIGHLIGHT.selectedBorder,
            pointerEvents: "none",
          }}
        >
          {!lockDraggable && (
            <>
              {!sectionLockDraggable && (
                <>
                  <div
                    style={handleStyle("top-left")}
                    onMouseDown={(e) => handleResize(e, "top-left")}
                  />
                  <div
                    style={handleStyle("top-right")}
                    onMouseDown={(e) => handleResize(e, "top-right")}
                  />
                  <div
                    style={handleStyle("bottom-left")}
                    onMouseDown={(e) => handleResize(e, "bottom-left")}
                  />
                  <div
                    style={handleStyle("bottom-right")}
                    onMouseDown={(e) => handleResize(e, "bottom-right")}
                  />
                  <div
                    style={edgeHandleStyle("left")}
                    onMouseDown={(e) => handleResize(e, "left")}
                  />
                  <div
                    style={edgeHandleStyle("right")}
                    onMouseDown={(e) => handleResize(e, "right")}
                  />
                  <div
                    style={edgeHandleStyle("top")}
                    onMouseDown={(e) => handleResize(e, "top")}
                  />
                  <div
                    style={edgeHandleStyle("bottom")}
                    onMouseDown={(e) => handleResize(e, "bottom")}
                  />
                </>
              )}

              <div style={toolbarStyle} data-draggable-toolbar="true">
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    sentPostMessage("duplicate", {
                      type: section?.type,
                      index,
                      subIndex,
                    });
                  }}
                  style={iconButtonStyle}
                  title="Duplicate"
                  data-draggable-icon="duplicate"
                >
                  <svg
                    fill="#000000"
                    viewBox="0 0 1920 1920"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <svg
                        fill="#000000"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g
                          id="SVGRepo_tracerCarrier"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></g>
                        <g id="SVGRepo_iconCarrier">
                          <path d="M4 22h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2zm2-9h3v-3h2v3h3v2h-3v3H9v-3H6v-2z"></path>
                          <path d="M20 2H8v2h12v12h2V4c0-1.103-.897-2-2-2z"></path>
                        </g>
                      </svg>
                    </g>
                  </svg>
                </div>

                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    sentPostMessage("lock", {
                      type: section?.type,
                      index,
                      subIndex,
                      isSubSection,
                    });
                  }}
                  style={iconButtonStyle}
                  title={
                    sectionLockDraggable
                      ? "Unlock element position"
                      : "Lock element position"
                  }
                  data-draggable-icon="lock"
                >
                  {sectionLockDraggable ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 3L21 21M17 10V8C17 5.23858 14.7614 3 12 3C11.0283 3 10.1213 3.27719 9.35386 3.75681M7.08383 7.08338C7.02878 7.38053 7 7.6869 7 8V10.0288M19.5614 19.5618C19.273 20.0348 18.8583 20.4201 18.362 20.673C17.7202 21 16.8802 21 15.2 21H8.8C7.11984 21 6.27976 21 5.63803 20.673C5.07354 20.3854 4.6146 19.9265 4.32698 19.362C4 18.7202 4 17.8802 4 16.2V14.8C4 13.1198 4 12.2798 4.32698 11.638C4.6146 11.0735 5.07354 10.6146 5.63803 10.327C5.99429 10.1455 6.41168 10.0647 7 10.0288M19.9998 14.4023C19.9978 12.9831 19.9731 12.227 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.773 10.0269 17.0169 10.0022 15.5977 10.0002M10 10H8.8C8.05259 10 7.47142 10 7 10.0288"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 14.5V16.5M7 10.0288C7.47142 10 8.05259 10 8.8 10H15.2C15.9474 10 16.5286 10 17 10.0288M7 10.0288C6.41168 10.0647 5.99429 10.1455 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C18.0057 10.1455 17.5883 10.0647 17 10.0288M7 10.0288V8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V10.0288"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  )}
                </div>

                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    sentPostMessage("delete", {
                      type: section?.type,
                      index,
                      subIndex,
                    });
                  }}
                  style={iconButtonStyle}
                  title="Delete"
                  data-draggable-icon="delete"
                >
                  <svg
                    fill="#000000"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path d="M5.755,20.283,4,8H20L18.245,20.283A2,2,0,0,1,16.265,22H7.735A2,2,0,0,1,5.755,20.283ZM21,4H16V3a1,1,0,0,0-1-1H9A1,1,0,0,0,8,3V4H3A1,1,0,0,0,3,6H21a1,1,0,0,0,0-2Z"></path>
                    </g>
                  </svg>
                </div>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsLayerPopoverOpen((prev) => !prev);
                  }}
                  style={iconButtonStyle}
                  title="Layers"
                  data-draggable-icon="layers"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <path
                        d="M17.4 10L21 12L17.4 14M17.4 10L12 13L6.6 10M17.4 10L21 8L12 3L3 8L6.6 10M6.6 10L3 12L6.6 14M17.4 14L21 16L12 21L3 16L6.6 14M17.4 14L12 17L6.6 14"
                        stroke="#000000"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>{" "}
                    </g>
                  </svg>

                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {isLayerPopoverOpen && (
                      <div
                        ref={popoverRef}
                        style={{
                          position: "absolute",
                          top: "-80%",
                          left: "180%",
                          width: "max-content",
                          background: "#FFFFFF",
                          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.8)",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        {[
                          {
                            label: "Bring forward",
                            zIndex: 2,
                            icon: (
                              <svg
                                fill="#000000"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="30%"
                                  y="30%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "rgb(115 115 115)",
                                  }}
                                />
                                <rect
                                  x="10%"
                                  y="10%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "#5c439f",

                                    // stroke: "#fff",
                                    // strokeWidth: "2",
                                  }}
                                />
                              </svg>
                            ),
                          },
                          {
                            label: "Bring to front",
                            zIndex: 3,
                            icon: (
                              <svg
                                fill="#000000"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="30%"
                                  y="30%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "rgb(115 115 115)",
                                  }}
                                />
                                <rect
                                  x="20%"
                                  y="20%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "rgb(181 181 181)",
                                  }}
                                />
                                <rect
                                  x="10%"
                                  y="10%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "#5c439f",
                                  }}
                                />
                              </svg>
                            ),
                          },
                          {
                            label: "Send backward",
                            zIndex: 1,
                            icon: (
                              <svg
                                fill="#000000"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="30%"
                                  y="30%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "#5c439f",
                                  }}
                                />
                                <rect
                                  x="10%"
                                  y="10%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "rgb(115 115 115)",
                                  }}
                                />
                              </svg>
                            ),
                          },
                          {
                            label: "Send to back",
                            zIndex: 0,
                            icon: (
                              <svg
                                fill="#000000"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="30%"
                                  y="30%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "#5c439f",
                                  }}
                                />
                                <rect
                                  x="20%"
                                  y="20%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "rgb(181 181 181)",
                                  }}
                                />
                                <rect
                                  x="10%"
                                  y="10%"
                                  width="50%"
                                  height="50%"
                                  rx="2"
                                  style={{
                                    fill: "rgb(125 125 125)",
                                  }}
                                />
                              </svg>
                            ),
                          },
                        ].map((layer, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "10px 15px",
                              cursor: "pointer",
                              fontSize: "15px",
                              color: "#000000",
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setCurrentLayer({ zIndex: layer.zIndex });
                              setUpdateLayer({ zIndex: layer.zIndex });
                              setIsLayerPopoverOpen((prev) => !prev);
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "rgba(0, 0, 0, 0.05)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "transparent")
                            }
                          >
                            <div style={{ width: "25px", height: "25px" }}>
                              {layer.icon}
                            </div>
                            <div
                              style={{
                                fontSize: "18px",
                              }}
                            >
                              {layer.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

const iconButtonStyle = {
  width: "24px",
  height: "24px",
  color: "black",
  cursor: "pointer",
  transition: "transform 0.2s ease",
  display: "inline-block",
  // zIndex: 9999,
  // pointerEvents: "auto",
  userSelect: "none",
  "&:hover": {
    transform: "scale(1.1)",
  },
  "&:focus": {
    outline: "none",
    boxShadow: "none"
  },
  "&:active": {
    transform: "scale(0.95)"
  }
};

const handleStyle = (position) => ({
  position: "absolute",
  width: "8px",
  height: "8px",
  backgroundColor: "white",
  border: "1px solid black",
  borderRadius: "50%",
  pointerEvents: "auto",
  cursor: getCursorForHandle(position),
  ...handlePositionStyle(position),
});

const edgeHandleStyle = (position) => ({
  position: "absolute",
  width: position === "left" || position === "right" ? "4px" : "20px",
  height: position === "top" || position === "bottom" ? "4px" : "20px",
  backgroundColor: "white",
  borderRadius: "20px",
  border: "1px solid black",
  pointerEvents: "auto",
  cursor: getCursorForEdge(position),
  ...handlePositionStyle(position),
});

const getCursorForHandle = (position) => {
  switch (position) {
    case "top-left":
    case "bottom-right":
      return "nwse-resize";
    case "top-right":
    case "bottom-left":
      return "nesw-resize";
    default:
      return "default";
  }
};

const getCursorForEdge = (position) => {
  switch (position) {
    case "left":
    case "right":
      return "ew-resize";
    case "top":
    case "bottom":
      return "ns-resize";
    default:
      return "default";
  }
};

const handlePositionStyle = (position) => {
  switch (position) {
    case "top-left":
      return { top: "-4px", left: "-4px" };
    case "top-right":
      return { top: "-4px", right: "-4px" };
    case "bottom-left":
      return { bottom: "-4px", left: "-4px" };
    case "bottom-right":
      return { bottom: "-4px", right: "-4px" };
    case "left":
      return { top: "50%", left: "-4px", transform: "translateY(-50%)" };
    case "right":
      return { top: "50%", right: "-4px", transform: "translateY(-50%)" };
    case "top":
      return { top: "-4px", left: "50%", transform: "translateX(-50%)" };
    case "bottom":
      return { bottom: "-4px", left: "50%", transform: "translateX(-50%)" };
    default:
      return {};
  }
};

const toolbarStyle = {
  position: "absolute",
  top: "-50px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "#fff",
  padding: "5px 10px",
  boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
  borderRadius: "5px",
  display: "flex",
  gap: "15px",
  pointerEvents: "auto",
  // zIndex: 9999,
  // userSelect: "none"
};

const rotateContainerStyle = {
  position: "absolute",
  top: "-100px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "#fff",
  padding: "5px",
  boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
  borderRadius: "100%",
  display: "flex",
};

export default DraggableWrapper;
